import React, { FC, useMemo } from "react";
import { rgba } from "polished";
import styled from "styled-components";
import { useEditMode } from "../../../contexts/edit";
import { Star } from "../../../icons/star";
import { Eye } from "../../../icons/eye";
import { SectionIdentifier } from "@services/tracpage/types/trac-page-section-id";
import { EMPTY_ITEMS_CONFIG } from "../../../constants/empty-items-config";
import { ItemsConfig } from "@services/tracpage/types/trac-page-items-config";
import { TracPageFeatureItemType } from "@services/tracpage/types/trac-page-feature-item";
import {
  deleteItem,
  insertItem,
  updateVisibility,
} from "../../../utils/items-config";
import { getSortMusicFn } from "../../../utils/items-sort";

type Props = {
  itemId: string;
  itemType: TracPageFeatureItemType;
  sectionId: SectionIdentifier;
  disabledState?: Partial<{
    feature: boolean;
    hide: boolean;
  }>;
};

export const VisisbilityButtons: FC<Props> = ({
  itemId,
  itemType,
  sectionId,
  disabledState,
}) => {
  const {
    editMode,
    draft,
    merch,
    events,
    editHandlers: { onFeature, onHide },
  } = useEditMode();

  const relevantItems = useMemo(() => {
    switch (sectionId) {
      case SectionIdentifier.MUSIC:
        return draft?.links.sort(getSortMusicFn(draft)) ?? [];
      case SectionIdentifier.MERCH:
        return merch;
      case SectionIdentifier.EVENTS:
        return events;
    }
  }, [draft, sectionId, merch, events]);

  if (!editMode || !draft) return null;

  const handleHide = () => {
    const itemsConfig = draft.itemsConfig ?? EMPTY_ITEMS_CONFIG;
    let currentSectionConfig = itemsConfig[sectionId];

    relevantItems.forEach((item, idx) => {
      if (!item.id) return;
      if (!currentSectionConfig[item.id]) {
        currentSectionConfig = insertItem(currentSectionConfig, item.id, {
          isHidden: false,
          order: idx + 1,
        });
      }
    });

    const currentItemConfig = currentSectionConfig[itemId];
    const newIsHidden = !currentItemConfig.isHidden;

    if (newIsHidden && itemId === draft.featuredItem?.id) {
      onFeature(itemId, itemType);
    }

    let updatedSectionConfig = updateVisibility(
      currentSectionConfig,
      itemId,
      newIsHidden
    );

    const redundantConfigKeys = Object.keys(updatedSectionConfig).filter(
      (id) => {
        const relevantItemIds = relevantItems
          .map((item) => item.id ?? "")
          .filter(Boolean);
        return !relevantItemIds.includes(id);
      }
    );

    redundantConfigKeys.forEach((id) => {
      updatedSectionConfig = deleteItem(updatedSectionConfig, id);
    });

    const updatedConfig: ItemsConfig = {
      ...itemsConfig,
      [sectionId]: updatedSectionConfig,
    };

    onHide(updatedConfig);
  };

  const isFeatured = draft?.featuredItem?.id === itemId;

  return (
    <ControlsWrapper>
      <button
        disabled={!!disabledState?.feature}
        className="icon-button"
        onClick={() => onFeature(itemId, itemType)}
      >
        <Star outline={!isFeatured} />
      </button>
      <button
        disabled={!!disabledState?.hide}
        className="icon-button"
        onClick={handleHide}
      >
        <Eye />
      </button>
    </ControlsWrapper>
  );
};

const ControlsWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;

  .icon-button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    aspect-ratio: 1;
    border-radius: 50%;
    background-color: ${({ theme }) => rgba(theme.colors.primary, 0.1)};
    cursor: pointer;
    padding: 0;
    overflow: hidden;
    transition: background-color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    &:disabled {
      opacity: 0.5;
      cursor: default;
      pointer-events: none;
    }

    &:hover {
      background-color: ${({ theme }) => rgba(theme.colors.primary, 0.3)};
    }

    &:focus,
    &:focus-within,
    &:focus-visible {
      outline: none;
    }
  }
`;
